/**
 * OS file picker — mini glass explorer (open file / folder / save).
 * Exposed as ctx.shell.dialog.openFile | openFolder | saveFile.
 */
import { useEffect, useState } from "react";

export type FileDialogMode = "open-file" | "open-folder" | "save-file";

export type FileDialogFilter = {
  label: string;
  /** extensions without dot, or "*" */
  extensions: string[];
};

export type FileDialogRequest = {
  id: string;
  mode: FileDialogMode;
  title: string;
  startPath: string;
  filename?: string;
  filters?: FileDialogFilter[];
  resolve: (path: string | null) => void;
};

type Listener = () => void;

class FileDialogStore {
  current: FileDialogRequest | null = null;
  /** epoch ms when current opened — ignore backdrop dismiss during same gesture */
  openedAt = 0;
  private listeners = new Set<Listener>();

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  private emit() {
    for (const fn of this.listeners) fn();
  }

  open(opts: {
    mode: FileDialogMode;
    title?: string;
    startPath?: string;
    filename?: string;
    filters?: FileDialogFilter[];
  }): Promise<string | null> {
    return new Promise((resolve) => {
      if (this.current) {
        // queue simply by replacing after settle — single picker for v0
        this.current.resolve(null);
      }
      this.openedAt = Date.now();
      this.current = {
        id: "fd_" + Date.now().toString(36),
        mode: opts.mode,
        title:
          opts.title ||
          (opts.mode === "open-folder"
            ? "Open Folder"
            : opts.mode === "save-file"
              ? "Save As"
              : "Open File"),
        startPath: opts.startPath || "/Users",
        filename: opts.filename,
        filters: opts.filters,
        resolve,
      };
      this.emit();
    });
  }

  settle(path: string | null) {
    const c = this.current;
    if (!c) return;
    this.current = null;
    this.openedAt = 0;
    this.emit();
    c.resolve(path);
  }

  /** Backdrop dismiss — ignore the opening pointer gesture (~same click). */
  dismissFromBackdrop() {
    if (!this.current) return;
    if (this.openedAt && Date.now() - this.openedAt < 600) return;
    this.settle(null);
  }

  dismiss() {
    this.settle(null);
  }
}

export const fileDialogStore = new FileDialogStore();

export function useFileDialog() {
  const [cur, setCur] = useState(fileDialogStore.current);
  useEffect(
    () => fileDialogStore.subscribe(() => setCur(fileDialogStore.current)),
    []
  );
  return cur;
}
